import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { ArrowRight, LifeBuoy, Sparkles } from 'lucide-react';
import { Inline } from './HelpRichText';
import { askHelp } from './helpCenterApi';
import { convertToSupportTicket, lastAppPage } from '../../utils/supportTicket';
import { useUserSettingsContext } from '../../context/UserSettingsContext';

/*
 * The Help Center's ask box: a fast help agent answers from the Help Center
 * articles (azfApi/helpCenter/ask.js), and each answer carries the one guide
 * that best fits the question as a plain "Suggested guide" link.
 */

/** Minimal markdown for answers: paragraphs, numbered and bulleted lists, inline marks. */
export function AnswerText({ text }) {
  const blocks = [];
  let list = null;
  String(text || '').split('\n').forEach((raw) => {
    const line = raw.trim();
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (numbered || bullet) {
      const type = numbered ? 'ol' : 'ul';
      if (!list || list.type !== type) { list = { type, items: [] }; blocks.push(list); }
      list.items.push((numbered || bullet)[1]);
      return;
    }
    list = null;
    if (line) blocks.push({ type: 'p', text: line.replace(/^#+\s+/, '') });
  });
  return (
    <div className="hc-answer-text">
      {blocks.map((b, i) => {
        if (b.type === 'p') return <p key={i}><Inline text={b.text} /></p>;
        const Tag = b.type;
        return <Tag key={i}>{b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}</Tag>;
      })}
    </div>
  );
}

const historyFrom = (turns) => turns
  .filter((t) => t.status === 'done')
  .flatMap((t) => [{ role: 'user', content: t.q }, { role: 'assistant', content: t.answer }]);

export default function HelpAsk({ question, route = lastAppPage() }) {
  const posthog = usePostHog();
  const { currentOrganization } = useUserSettingsContext();
  const [turns, setTurns] = useState([]);
  const [ticket, setTicket] = useState('idle'); // idle | drafting | opened
  const [followUp, setFollowUp] = useState('');
  const turnsRef = useRef(turns);
  turnsRef.current = turns;

  const ask = (q, prior) => {
    const id = Date.now();
    setTurns([...prior, { id, q, status: 'loading' }]);
    askHelp({ question: q, history: historyFrom(prior), route })
      .then((res) => {
        setTurns((cur) => cur.map((t) => (t.id === id ? { ...t, status: 'done', answer: res.answer, suggestion: res.suggestion } : t)));
        posthog?.capture?.('help_ask', { followUp: prior.length > 0, suggested: res.suggestion?.slug || null });
      })
      .catch((err) => {
        const message = err?.response?.data?.error || 'The help assistant is unavailable right now.';
        setTurns((cur) => cur.map((t) => (t.id === id ? { ...t, status: 'error', message } : t)));
      });
  };

  // A new question from the search box starts a new thread. One already
  // answered in ⌘K arrives with its answer, so it isn't asked twice.
  const { state: navState } = useLocation();
  useEffect(() => {
    const q = question.trim();
    if (!q) return;
    const seeded = navState?.helpAnswer;
    if (seeded?.q === q && seeded.answer) {
      setTurns([{ id: Date.now(), q, status: 'done', answer: seeded.answer, suggestion: seeded.suggestion || null }]);
      return;
    }
    ask(q, []);
  }, [question]); // eslint-disable-line react-hooks/exhaustive-deps

  const busy = turns.some((t) => t.status === 'loading');

  // The whole thread becomes the ticket: the latest question plus what was tried.
  const convert = async () => {
    const done = turns.filter((t) => t.status !== 'loading');
    const latest = done[done.length - 1];
    if (!latest) return;
    setTicket('drafting');
    const opened = await convertToSupportTicket({
      question: latest.q,
      history: historyFrom(done.slice(0, -1)).concat(latest.status === 'done' ? [{ role: 'user', content: latest.q }, { role: 'assistant', content: latest.answer }] : []),
      orgName: currentOrganization?.name || null,
    });
    setTicket(opened ? 'opened' : 'idle');
    posthog?.capture?.('help_ticket_converted', { turns: done.length, opened });
  };

  return (
    <section className="hc-answer" aria-live="polite" aria-busy={busy}>
      <div className="hc-answer-head">
        <span className="hc-answer-icon"><Sparkles size={14} aria-hidden="true" /></span>
        <h2>Answer</h2>
      </div>

      {turns.map((t, i) => (
        <div key={t.id} className="hc-turn">
          {i > 0 ? <p className="hc-turn-q">{t.q}</p> : null}
          {t.status === 'loading' ? (
            <div className="hc-answer-loading" aria-label="Finding the answer">
              <span className="lodge-skeleton" style={{ width: '82%' }} />
              <span className="lodge-skeleton" style={{ width: '64%' }} />
              <span className="lodge-skeleton" style={{ width: '71%' }} />
            </div>
          ) : t.status === 'error' ? (
            <p className="hc-empty">{t.message}</p>
          ) : (
            <>
              <AnswerText text={t.answer} />
              {t.suggestion?.url ? (
                <Link className="hc-suggested" to={t.suggestion.url}>
                  <span className="hc-suggested-label">Suggested guide</span>
                  <span className="hc-suggested-title">{t.suggestion.title}</span>
                  <ArrowRight size={14} aria-hidden="true" />
                </Link>
              ) : null}
            </>
          )}
        </div>
      ))}

      <form
        className="hc-followup"
        onSubmit={(e) => {
          e.preventDefault();
          const q = followUp.trim();
          if (!q || busy) return;
          setFollowUp('');
          ask(q, turnsRef.current.filter((t) => t.status === 'done'));
        }}
      >
        <label htmlFor="hc-followup-input" className="hc-visually-hidden">Ask a follow-up</label>
        <input
          id="hc-followup-input"
          type="text"
          value={followUp}
          placeholder="Ask a follow-up…"
          onChange={(e) => setFollowUp(e.target.value)}
          disabled={busy}
        />
        <button type="submit" disabled={busy || !followUp.trim()}>Ask</button>
      </form>

      <div className="hc-ticket">
        {ticket === 'opened' ? (
          <span>Your message is ready in support — review it and press send.</span>
        ) : (
          <>
            <span>Didn’t solve it?</span>
            <button type="button" onClick={convert} disabled={busy || ticket === 'drafting' || !turns.length}>
              <LifeBuoy size={14} aria-hidden="true" />
              {ticket === 'drafting' ? 'Drafting your message…' : 'Convert to a support ticket'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
